import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { postAdminChat } from '../api/client';
import type { ChatUsage, ToolTraceEntry } from '../api/types';
import MarkdownView from '../components/analyst/MarkdownView';
import ToolTrace from '../components/analyst/ToolTrace';

interface UiMessage {
  role: 'user' | 'assistant';
  content: string;
  toolTrace?: ToolTraceEntry[];
  usage?: ChatUsage;
  /** Preloaded report (Discuss hand-off) — rendered with a "cached analysis report" caption. */
  seeded?: boolean;
}

type ContextKey = 'matchId' | 'storyId' | 'pebbleId';

export default function AnalystPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const matchId = searchParams.get('matchId') ?? undefined;
  const storyId = searchParams.get('storyId') ?? undefined;
  const pebbleId = searchParams.get('pebbleId') ?? undefined;
  const seededReport = (location.state as { report?: string } | null)?.report;

  const [messages, setMessages] = useState<UiMessage[]>(() =>
    seededReport ? [{ role: 'assistant', content: seededReport, seeded: true }] : []
  );
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const bottomRef = useRef<HTMLDivElement | null>(null);

  const context =
    matchId || storyId || pebbleId ? { matchId, storyId, pebbleId } : undefined;

  // Auto-scroll on new messages (and when the thinking row appears).
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length, sending]);

  async function send() {
    const text = input.trim();
    if (!text || sending) return;
    const next: UiMessage[] = [...messages, { role: 'user', content: text }];
    setMessages(next);
    setInput('');
    setSending(true);
    setError(null);
    try {
      const res = await postAdminChat({
        // History (incl. any seeded report) is resent verbatim each turn.
        messages: next.map(({ role, content }) => ({ role, content })),
        context,
      });
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: res.reply, toolTrace: res.toolTrace, usage: res.usage },
      ]);
    } catch (e) {
      // The user message stays in the list; retry = type it again.
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSending(false);
    }
  }

  function newConversation() {
    setMessages([]);
    setError(null);
  }

  function clearContext(key: ContextKey) {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete(key);
    setSearchParams(nextParams, { replace: true });
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Analyst</h1>
        <button
          type="button"
          onClick={newConversation}
          disabled={sending || messages.length === 0}
          className="btn btn-secondary disabled:opacity-50"
        >
          New conversation
        </button>
      </div>

      {/* Context chips */}
      {context && (
        <div className="flex items-center gap-2 flex-wrap">
          {matchId && (
            <ContextChip
              label="match"
              id={matchId}
              to={`/matches/${matchId}`}
              onClear={() => clearContext('matchId')}
            />
          )}
          {storyId && (
            <ContextChip
              label="story"
              id={storyId}
              to={`/stories/${storyId}`}
              onClear={() => clearContext('storyId')}
            />
          )}
          {pebbleId && (
            <ContextChip label="pebble" id={pebbleId} onClear={() => clearContext('pebbleId')} />
          )}
        </div>
      )}

      {/* Message list */}
      <div className="space-y-4">
        {messages.length === 0 && !sending && (
          <div className="text-sm text-gray-500 italic">
            Ask why a match scored what it scored, what evidence is missing, or anything else
            about the pipeline.
          </div>
        )}
        {messages.map((m, i) =>
          m.role === 'user' ? (
            <div key={i} className="flex justify-end">
              <div className="bg-pebbl-600 text-white rounded-lg px-4 py-2 max-w-[80%] whitespace-pre-wrap text-sm">
                {m.content}
              </div>
            </div>
          ) : (
            <div key={i} className="flex justify-start">
              <div className="card max-w-[90%]">
                <MarkdownView>{m.content}</MarkdownView>
                <ToolTrace trace={m.toolTrace ?? []} />
                {m.seeded ? (
                  <div className="text-xs text-gray-400 mt-2">cached analysis report</div>
                ) : (
                  m.usage && (
                    <div className="text-xs text-gray-400 mt-2">
                      {m.usage.iterations} iter · {m.usage.inputTokens}/{m.usage.outputTokens} tok
                    </div>
                  )
                )}
              </div>
            </div>
          )
        )}
        {sending && (
          <div className="text-gray-500 animate-pulse text-sm">Analyst is thinking…</div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* Composer */}
      <div className="flex items-end gap-2">
        <textarea
          rows={2}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              send();
            }
          }}
          placeholder="Ask the analyst… (Enter to send, Shift+Enter for newline)"
          disabled={sending}
          className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm resize-none disabled:bg-gray-50"
        />
        <button
          type="button"
          onClick={send}
          disabled={sending || !input.trim()}
          className="btn btn-primary disabled:opacity-50"
        >
          Send
        </button>
      </div>
    </div>
  );
}

function ContextChip({
  label,
  id,
  to,
  onClear,
}: {
  label: string;
  id: string;
  to?: string;
  onClear: () => void;
}) {
  const display = id.length > 10 ? `${id.slice(0, 10)}…` : id;
  const body = (
    <>
      {label} <code className="text-xs">{display}</code>
    </>
  );
  return (
    <span className="inline-flex items-center gap-2 bg-pebbl-600 text-white px-3 py-1 rounded-full text-sm">
      {to ? (
        <Link to={to} className="hover:underline">
          {body}
        </Link>
      ) : (
        <span>{body}</span>
      )}
      <button
        type="button"
        onClick={onClear}
        className="text-pebbl-100 hover:text-white"
        aria-label={`Remove ${label} context`}
      >
        ×
      </button>
    </span>
  );
}
